import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Popover, FormControlLabel, Checkbox, FormGroup } from '@mui/material';
import { FilterListIcon, InfoIcon } from 'icons/materialIcons';
import ActivityItem from './ActivityItem';

const RecentActivity = ({ activities = [] }) => {
    console.group('🔍 RecentActivity Component - COMPREHENSIVE DEBUG');
    console.log("📊 Raw activities received:", activities);
    console.log("📊 Activities count:", activities?.length || 0);
    console.log("📊 Activities array check:", Array.isArray(activities));

    if (activities && activities.length > 0) {
        console.log("📊 First activity detailed breakdown:");
        const firstActivity = activities[0];
        console.log("  - Raw activity object:", firstActivity);
        console.log("  - Activity keys:", Object.keys(firstActivity || {}));
        console.log("  - Activity type:", firstActivity?.type);
        console.log("  - Activity user:", firstActivity?.user);
        console.log("  - Activity metadata:", firstActivity?.metadata);
        console.log("  - Activity metadata keys:", Object.keys(firstActivity?.metadata || {}));

        if (firstActivity?.metadata) {
            console.log("  - User identification sources:", {
                user: firstActivity?.user,
                metadataUserName: firstActivity?.metadata?.userName,
                metadataName: firstActivity?.metadata?.name,
                metadataDisplayName: firstActivity?.metadata?.displayName,
                metadataEmail: firstActivity?.metadata?.email,
                originalTapData: firstActivity?.metadata?.originalTapData
            });
        }
    }
    console.groupEnd();

    // Add useEffect to verify card holder names
    useEffect(() => {
        console.group('🔍 RecentActivity - Card Holder Name Verification');
        if (activities && activities.length > 0) {
            activities.forEach((activity, index) => {
                if (activity.type === 'lead_capture' || activity.type === 'lead') {
                    console.log(`\nActivity ${index + 1}:`, {
                        type: activity.type,
                        leadName: activity.actorName,
                        cardHolderName: activity.metadata?.cardHolderName,
                        cardOwnerName: activity.metadata?.cardOwnerName,
                        cardName: activity.metadata?.cardName,
                        action: activity.action,
                        rawMetadata: activity.metadata
                    });
                }
            });
        } else {
            console.log('No activities to verify');
        }
        console.groupEnd();
    }, [activities]);

    // Filter state management
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    const [filters, setFilters] = useState({
        taps: true,
        views: true,
        connections: true
    });

    const handleFilterClick = (event) => {
        setFilterAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setFilterAnchorEl(null);
    };

    const handleFilterChange = (filterType) => (event) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: event.target.checked
        }));
    };

    const isFilterOpen = Boolean(filterAnchorEl);// Function to process activities data    // Process activities for display - Handle structured data from backend
    const processActivities = (activities) => {
        if (!activities || !Array.isArray(activities)) return [];

        console.log('Processing activities with structured data:', activities.slice(0, 3));

        const extractName = (possibleName) => {
            if (!possibleName) return null;
            if (typeof possibleName !== 'string') return null;
            const cleaned = possibleName.trim();
            if (cleaned === '' || cleaned === 'Someone' || cleaned === 'undefined' || cleaned === 'null' || cleaned.toLowerCase() === 'user') {
                return null;
            }
            return cleaned;
        };        // Format messages based on activity type and structured data
        const formatActivityMessage = (activity) => {
            const actorName = extractName(activity.actorName) || 'Someone';
            const targetName = extractName(activity.targetName);

            switch (activity.type) {
                case 'card_view':
                    if (targetName) {
                        return {
                            user: `${targetName}'s card was viewed`,
                            action: ""
                        };
                    }
                    return {
                        user: 'Your card was viewed',
                        action: ""
                    };

                case 'link_tap':
                    const linkType = activity.linkType || 'link';
                    if (targetName && linkType) {
                        return {
                            user: `${targetName}'s ${linkType}`,
                            action: "was tapped"
                        };
                    }
                    return {
                        user: `Someone's ${linkType}`,
                        action: "was tapped"
                    };

                case 'contact_download':
                    if (targetName) {
                        return {
                            user: `${targetName}'s Connect`,
                            action: "was tapped"
                        };
                    }
                    return {
                        user: "Your Connect",
                        action: "was tapped"
                    };

                case 'lead_capture':
                case 'lead':
                    // Use the card holder name from metadata
                    const cardHolderName = activity.metadata?.cardHolderName;
                    if (cardHolderName) {
                        return {
                            user: actorName,
                            action: `connected with ${cardHolderName}`
                        };
                    }
                    return {
                        user: actorName,
                        action: 'connected with a card'
                    };

                case 'share':
                    if (actorName) {
                        return {
                            user: actorName,
                            action: `shared a card`
                        };
                    }
                    return {
                        user: 'Someone',
                        action: 'shared a card'
                    };

                default:
                    return {
                        user: actorName,
                        action: 'performed an action'
                    };
            }
        };

        return activities.map(activity => {
            if (!activity) {
                console.warn('Skipping invalid activity object:', activity);
                return null;
            }

            const formattedMessage = formatActivityMessage(activity);

            return {
                type: activity.type,
                user: formattedMessage.user,
                action: formattedMessage.action,
                timestamp: activity.timestamp || activity.createdAt || activity.date,
                metadata: {
                    ...activity.metadata,
                    actorName: activity.actorName,
                    targetName: activity.targetName,
                    linkType: activity.linkType
                }
            };
        }).filter(activity => activity && activity.timestamp && activity.action && activity.type);
    };    // Get activities to display - FIXED: Always prioritize real data with actual names and apply filters
    const displayActivities = activities.length > 0
        ? processActivities(activities).filter(activity => {            // Apply filters based on activity type
            switch (activity.type) {
                case 'link_tap':
                case 'tap':
                case 'contact_download':
                case 'contact_save':
                case 'save_contact':
                    return filters.taps; // Contact downloads and link taps show under taps filter
                case 'card_view':
                case 'view':
                    return filters.views;
                case 'lead':
                case 'lead_capture':
                case 'lead_captured':
                    return filters.connections;
                default:
                    return true; // Show other activities by default
            }
        })
            .slice(0, 5)
        : [];

    return (
        <Box
            sx={{
                width: '338px',
                height: '414px',
                padding: 0,
                position: 'relative',
                maxHeight: '100%',
                marginLeft: 'auto',
                paddingTop: 0,
                borderRadius: '18px',
                backgroundColor: '#ffffff'
            }}
        >
            <Box
                sx={{
                    height: '76px',
                    display: 'flex',
                    padding: '0px 14px 0px 36px',
                    alignItems: 'center',
                    borderRadius: '18px',
                    justifyContent: 'space-between'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Typography sx={{ fontSize: '12px', fontWeight: 500 }}>
                        Recent Activity
                    </Typography>
                    <Tooltip title="Recent interactions with your card">
                        <InfoIcon sx={{ fontSize: 16, color: '#828282' }} />
                    </Tooltip>
                </Box>                <IconButton onClick={handleFilterClick}>
                    <FilterListIcon />
                </IconButton>

                <Popover
                    open={isFilterOpen}
                    anchorEl={filterAnchorEl}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                >
                    <Box sx={{ p: 2, minWidth: '200px' }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Filter Activity
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters.taps}
                                        onChange={handleFilterChange('taps')}
                                        size="small"
                                    />
                                }
                                label="Taps"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters.views}
                                        onChange={handleFilterChange('views')}
                                        size="small"
                                    />
                                }
                                label="Views"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={filters.connections}
                                        onChange={handleFilterChange('connections')}
                                        size="small"
                                    />
                                }
                                label="Connections"
                                sx={{ '& .MuiFormControlLabel-label': { fontSize: '14px' } }}
                            />
                        </FormGroup>
                    </Box>
                </Popover>
            </Box>

            <Box
                sx={{
                    height: '336px',
                    overflow: 'auto',
                    paddingRight: '4px',
                    '&::-webkit-scrollbar': {
                        width: '5px'
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#ffffff',
                        borderRadius: '5px'
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#adadad',
                        borderRadius: '5px'
                    }
                }}
            >
                {displayActivities.length > 0 ? (
                    displayActivities.map((activity, index) => (
                        <ActivityItem
                            key={index}
                            {...activity}
                        />
                    ))
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%'
                        }}
                    >
                        <Typography sx={{ color: '#828282', fontSize: '13px' }}>
                            No recent activity
                        </Typography>
                    </Box>
                )}
            </Box>
        </Box>
    );
};

export default RecentActivity;